import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useState } from "react";
import backgroundWhitePurple from "../../../assets/white-and-purple-background.jpg";
import passwordIcon from "../../../assets/password-icon.png";

import { customer } from "localStorage";
import { isMobile } from "react-device-detect";
import Copyright from "components/Copyright";
import { ClientSetPassword } from "api/client";
import { useNavigate } from "react-router-dom";

const SetPassword = ({ updateCustomerState }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrengthText, setPasswordStrengthText] = useState("");
  const [passwordStrengthColor, setPasswordStrengthColor] = useState("red");

  const _customer = customer();
  const navigate = useNavigate();

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);

    // Password strength logic with mixed letters and numbers validation
    const hasLetters = /[a-zA-Z]/.test(value);
    const hasNumbers = /\d/.test(value);

    if (value.length < 8 || !hasLetters || !hasNumbers) {
      setPasswordStrengthText(
        "Weak (must at least 8 characters include letters and numbers)"
      );
      setPasswordStrengthColor("red");
    } else if (value.length < 10) {
      setPasswordStrengthText("Moderate");
      setPasswordStrengthColor("orange");
    } else {
      setPasswordStrengthText("Strong");
      setPasswordStrengthColor("green");
    }
  };

  const handleSubmit = () => {
    setError("");
    setSuccess(false);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const hasLetters = /[a-zA-Z]/.test(password);
    const hasNumbers = /\d/.test(password);

    if (password.length < 8 || !hasLetters || !hasNumbers) {
      setError(
        "Password must be at least 8 characters long and include both letters and numbers."
      );
      return;
    }

    setLoading(true);
    // Call the API to set the password
    ClientSetPassword(password).then((response) => {
      console.log("🚀 ~ ClientSetPassword ~ response:", response);
      setLoading(false);

      if (response.status !== 200) {
        setError(response.message);
        return;
      } else {
        setSuccess(true);
        setError("");
        setTimeout(() => {
          delete _customer.emptyPassword;
          delete _customer.existingCustomer;

          localStorage.setItem("customer", JSON.stringify(_customer));
          updateCustomerState(_customer);
        }, 5000);
        navigate("/my-account");
      }
    });
  };

  return (
    <Grid
      direction="column"
      alignItems="center"
      container
      component="main"
      sx={{
        height: "100vh",
        backgroundImage: `url(${backgroundWhitePurple})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Box
        borderRadius={isMobile ? undefined : 5}
        mt={isMobile ? 0 : 3}
        component={Paper}
        elevation={6}
        square
        sx={{
          height: isMobile ? "100vh" : "700px",
          width: isMobile ? "100vw" : "400px",
          display: "flex", // Enable flexbox on the Grid
          flexDirection: "column", // Stack children vertically
        }}
      >
        <Box
          sx={{
            my: 4,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexGrow: 1, // This will make the content box take up available vertical space
          }}
        >
          <Box
            component="img"
            src={passwordIcon}
            alt="Logo"
            sx={{
              maxWidth: "300px",
              height: "150px",
              display: "block",
              margin: "0 auto",
            }}
          />
          {!success && (
            <Box component="form" noValidate onSubmit={handleSubmit} mt={2}>
              <Grid container spacing={1}>
                <Grid item xl={12} md={12} xs={12}>
                  <Typography variant="body1" align="center">
                    Welcome, {_customer.name}!
                  </Typography>
                  <Typography variant="body1" align="center">
                    Please set a password for your account
                  </Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ mt: 1 }}
                    align="center"
                  >
                    Your whatsapp number is: {_customer.whatsapp}
                  </Typography>
                </Grid>
                <Grid item xl={12} md={12} xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    margin="normal"
                  />
                  <Typography color={passwordStrengthColor} variant="body2">
                    {passwordStrengthText}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    margin="normal"
                  />
                  {error && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {error}
                    </Typography>
                  )}
                  {success && (
                    <Typography
                      color="success.main"
                      variant="body2"
                      sx={{ mt: 1 }}
                    >
                      Password set successfully!
                    </Typography>
                  )}
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    sx={{ my: 1 }}
                  >
                    Please remember your password for future logins.{" "}
                  </Typography>

                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                      padding: 1,
                      mt: 1,
                      mb: 1,
                      fontSize: "1rem",
                      letterSpacing: "0.1em",
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : "Set Password"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
        <Copyright sx={{ pb: 3 }} />
      </Box>
    </Grid>
  );
};

export default SetPassword;
